import { memo, type ReactNode, useEffect, useState } from 'react'

import { Alert, Button, Checkbox, Group, Text, TextInput } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import isEmail from 'validator/es/lib/isEmail'
import { z } from 'zod'

import { IconInfoCircle } from '@repo/common/components/Icons'
import {
  useLogin,
  useRequestResetLink,
  useSignup,
} from '@repo/common/queries/auth'

import { form_input_props, LoginSignupForm } from '../LoginSignupForm'

const password_message = {
  message:
    'Password must be at least 16 characters OR at least 8 characters including a number and a letter',
}
export const password_zod = z.union([
  z.string().trim().min(16, password_message).max(80),
  z
    .string()
    .trim()
    .min(8, password_message)
    .max(80)
    .regex(/\d/, password_message),
])

const schema = z.union([
  z.object({
    mode: z.literal('login'),
    email: z.string().refine(isEmail, { message: 'Invalid email address' }),
    password: password_zod,
  }),
  z.object({
    mode: z.literal('signup'),
    email: z.string().refine(isEmail, { message: 'Invalid email address' }),
    password: password_zod,
  }),
])

export const LoginSignup = memo<{
  starting_mode?: 'login' | 'signup'
  onSuccess?: () => void
  initial_values_override?: { email?: string; password?: string }
  without_header?: boolean
  header_as_link?: boolean
  pre_register?: (already_registered: boolean) => void
  children?: ReactNode
  // eslint-disable-next-line complexity, max-lines-per-function
}>(function LoginSignup({
  starting_mode = 'login',
  onSuccess,
  initial_values_override,
  without_header,
  header_as_link,
  pre_register,
  children,
}) {
  const form = useForm({
    initialValues: {
      email: '',
      password: '',
      newsletter_opt_in: true,
      ...initial_values_override,
      mode: starting_mode,
    },
    validate: zodResolver(schema),
  })
  const [show_reset, set_show_reset] = useState(false)
  const isLogin = form.values.mode === 'login'

  const login = useLogin()
  const signup = useSignup()
  const reset_link = useRequestResetLink()
  const isPending = login.isPending || signup.isPending
  const signup_text = pre_register ? 'Pre-Register' : 'Signup'

  const email_exists = signup.error?.message.toLowerCase() == 'email exists'

  useEffect(() => {
    if (email_exists) {
      pre_register?.(true)
      form.setFieldValue('mode', 'login')
    }
    // form changes every time the component renders, so it's safe to use it here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pre_register, email_exists])

  return (
    <LoginSignupForm
      form={form}
      without_header={without_header}
      header_as_link={header_as_link}
      action={isLogin ? login : signup}
      button_text={isLogin ? 'Login' : signup_text}
      onSuccess={onSuccess}
      after_button={
        <Group justify="flex-start">
          {isLogin && !login.isError && !show_reset && (
            <Button
              type="button"
              color="primary"
              variant="subtle"
              size="sm"
              onClick={() => set_show_reset(true)}
            >
              Forgot password?
            </Button>
          )}
          <Button
            type="button"
            color="primary"
            variant="light"
            size="sm"
            onClick={() =>
              form.setFieldValue('mode', isLogin ? 'signup' : 'login')
            }
          >
            {isLogin ? 'Signup' : 'Login'} instead?
          </Button>
        </Group>
      }
    >
      <>
        {children}
        {email_exists && (
          <Alert
            variant="light"
            color="blue"
            title="Email already exists"
            icon={<IconInfoCircle />}
          >
            <Text>Email already exists try Login instead?</Text>
          </Alert>
        )}
        <TextInput
          label="Email"
          {...form.getInputProps('email')}
          disabled={isPending}
          {...form_input_props}
        />
        <TextInput
          label="Password"
          type="password"
          {...form.getInputProps('password')}
          disabled={isPending}
          {...form_input_props}
        />
        <Checkbox
          label="Get the latest updates and newsletters"
          {...form.getInputProps('newsletter_opt_in', { type: 'checkbox' })}
        />
        {isLogin && (login.isError || show_reset) && (
          <Group pl="lg">
            <Text>Forgot password?</Text>
            <Button
              loading={reset_link.isPending}
              disabled={reset_link.isSuccess}
              onClick={() => reset_link.mutate(form.getValues())}
            >
              {reset_link.isSuccess ? 'Sent!' : 'Send reset email'}
            </Button>
          </Group>
        )}
      </>
    </LoginSignupForm>
  )
})
